import { ArrowLeftOutlined } from "@ant-design/icons";
import { Col, Row, Spinner, Button as ReactstrapButton } from "reactstrap";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import React, { useState, useMemo } from "react";
import { CustomButton, showToaster, toasterTypes } from "../../widgets";
import { FieldItem } from "../../widgets/fields";
import { pdfExportFormElements } from "./FormElements";
import useFieldItem from "../../widgets/fields/UseFieldItem";
import CustomTable from "../../widgets/table";

// Define types for field and record
type Field = {
    id: string;
    field_name: string;
    field_selected_value: string;
};

type RecordItem = {
    id: string;
    fields: Field[];
};

interface PrintPdfProps {
    data: RecordItem[];
    selectedRecords: string[];
    onClose: () => void;
    formData: any;
}

const PrintPdfComponent: React.FC<PrintPdfProps> = ({ data, selectedRecords, onClose, formData }) => {
    const [loading, setLoading] = useState<boolean>(false);

    const initialValues = useMemo(() => ({
        pageOrientation: { label: "portrait", value: "portrait" },
        columnsPerPage: 4,
        rowsPerPage: 20
    }), []);

    const [values, fields, handleChange, { validateValues, updateValue }] = useFieldItem(pdfExportFormElements, initialValues, { onValueChange });

    // Only include records selected by the user - memoized to prevent recalculation
    const selectedRecordObjects: RecordItem[] = useMemo(() =>
        data.filter((record, index) => selectedRecords.includes(index.toString())),
        [data, selectedRecords]
    );

    function onValueChange(name, value, values, fieldValues = null) {
        return [
            {
                ...values,
                [name]: value,
            },
        ];
    }

    // ---- Normalize data for PDF and preview - memoized ----
    const { fieldNames, flatRows } = useMemo(() => {
        const fieldNamesSet = new Set<string>();
        selectedRecordObjects.forEach(record => {
            record.fields?.forEach(field => fieldNamesSet.add(field.field_name));
        });
        const fieldNames = Array.from(fieldNamesSet);

        const flatRows: any = selectedRecordObjects.map((record, index) => {
            const row: any = { slNo: index + 1 };
            fieldNames.forEach(name => {
                const field = record.fields?.find(f => f.field_name === name);
                row[name] = field?.field_selected_value || "";
            });
            return row;
        });

        return { fieldNames, flatRows };
    }, [selectedRecordObjects]);

    // ---- Utility function to chunk arrays ----
    const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
        const chunks: T[][] = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    };

    // ---- Build table segments for PDF ----
    const buildTableSlices = (
        fieldNames: string[],
        flatRows: any[]
    ): { headers: string[]; rows: (string | number)[][] }[] => {
        const columnGroups = chunkArray(fieldNames, values.columnsPerPage);
        const rowChunks = chunkArray(flatRows, values.rowsPerPage);

        const tableSlices: { headers: string[]; rows: (string | number)[][] }[] = [];

        columnGroups.forEach(colGroup => {
            rowChunks.forEach(rowChunk => {
                const headers = ["Sl. No", ...colGroup];
                const rows = rowChunk.map(record => {
                    const row: (string | number)[] = [record.slNo];
                    colGroup.forEach(fieldName => {
                        row.push(record[fieldName]);
                    });
                    return row;
                });
                tableSlices.push({ headers, rows });
            });
        });

        return tableSlices;
    };

    // ---- PDF generation ----
    const generatePDF = async () => {
        if (!selectedRecordObjects.length) {
            showToaster(toasterTypes.WARNING, "No records selected.");
            return;
        }
        setLoading(true);

        try {
            if (!fieldNames.length) {
                showToaster(toasterTypes.ERROR, "No fields found in selected records.");
                setLoading(false);
                return;
            }
            const tableSlices = buildTableSlices(fieldNames, flatRows);

            // Handle pageOrientation value - it can be either object or string
            const orientation = values.pageOrientation?.value || values.pageOrientation;

            const doc = new jsPDF({
                orientation: orientation === "landscape" ? "l" : "p",
                unit: "mm",
                format: "a4",
            });

            const pageHeight = doc.internal.pageSize.getHeight();
            const pageWidth = doc.internal.pageSize.getWidth();

            tableSlices.forEach((slice, index) => {
                if (index > 0) doc.addPage();

                autoTable(doc, {
                    head: [slice.headers],
                    body: slice.rows,
                    startY: 20,
                    margin: { top: 15, bottom: 10, left: 10, right: 10 },
                    showHead: "everyPage",
                    styles: {
                        fontSize: 9,
                        cellPadding: 3,
                        textColor: [0, 0, 0],
                        overflow: "linebreak",
                        halign: "left",
                        valign: "middle",
                    },
                    headStyles: {
                        fillColor: [41, 128, 185], // blue
                        textColor: [255, 255, 255],
                        fontStyle: "bold",
                        fontSize: 10,
                        halign: "center",
                    },
                    bodyStyles: {
                        fillColor: [245, 245, 245],
                        textColor: [0, 0, 0],
                    },
                    alternateRowStyles: {
                        fillColor: [255, 255, 255],
                    },
                    columnStyles: {
                        0: { cellWidth: 12, halign: "center" },
                    },
                    didDrawPage: () => {
                        // Footer
                        const pageCount = doc.internal.pages.length - 1;
                        const pageNum = `Page ${pageCount}`;
                        doc.setFontSize(8);
                        doc.text(pageNum, pageWidth - 20, pageHeight - 10);
                    },
                });
            });

            doc.save(`form_export_${Date.now()}.pdf`);
            showToaster(toasterTypes.SUCCESS, "PDF generated and downloaded successfully!");
        } catch (e) {
            console.error(e);
            showToaster(toasterTypes.ERROR, e?.message || "Error generating PDF");
        } finally {
            setLoading(false);
        }
    };

    const tableProps = useMemo(
        () => [
            {
                id: "slNo",
                label: "Sl. No",
                accessor: "slNo",
                sort: "asc",
            },
            ...fieldNames.map((name) => ({
                id: name,
                label: name,
                accessor: name,
            })),
        ],
        [fieldNames]
    );


    // ---- UI Render ----
    return (
        <>
            <Row className="page-header m-0">
                <Col className={window.innerWidth > 750 ? "project-title" : "project-title-mob"} md={11}>
                    <ArrowLeftOutlined style={{ color: "#fff", fontSize: "24px", margin: "0px 10px" }} onClick={onClose} />
                    <span style={{ color: "#fff", fontSize: "24px", margin: "0px 10px" }}>Print PDF</span>
                </Col>
            </Row>
            <div style={{ padding: "20px" }} className="printpdf-page-container">
                {loading && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <Spinner color="primary" />
                        <p>Generating PDF...</p>
                    </div>
                )}
                <div style={{ background: "#f5f5f5", padding: "20px", borderRadius: "8px", marginBottom: "20px" }}>
                    <h3>Selected Records: {selectedRecordObjects.length}</h3>
                    {selectedRecordObjects.length > 0 ? (
                        <>
                            {/* Preview */}
                            <div style={{ marginBottom: "20px", overflowX: "auto" }}>
                                <h4>Preview:</h4>
                                <CustomTable
                                    tableProps={tableProps}
                                    data={flatRows}
                                    // optional flags – keep them simple for preview
                                    isSort={false}
                                    isSelect={false}
                                    isDraggable={false}
                                    isSearch={false}
                                    isFilter={false}
                                    showPagination={false}
                                    // stub callbacks that your Table expects but you don't need here
                                    onAction={() => { }}
                                    getRearrangeData={() => { }}
                                    getFilteredData={() => { }}
                                    rules={[]}
                                    formId={null}
                                />

                            </div>
                            {/* PDF Options */}
                            <div style={{ background: "#fff", padding: "15px", borderRadius: "8px", marginBottom: "20px" }}>
                                <h4>PDF Export Options</h4>
                                <Row>
                                    <Col md={4}>
                                        <FieldItem
                                            {...pdfExportFormElements.pageOrientation}
                                            value={values.pageOrientation}
                                            onChange={(...e) => handleChange("pageOrientation", ...e)}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <FieldItem
                                            {...pdfExportFormElements.columnsPerPage}
                                            value={values.columnsPerPage}
                                            onChange={(...e) => handleChange("columnsPerPage", ...e)}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <FieldItem
                                            {...pdfExportFormElements.rowsPerPage}
                                            value={values.rowsPerPage}
                                            onChange={(...e) => handleChange("rowsPerPage", ...e)}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </>
                    ) : (
                        <p style={{ color: "#999", textAlign: "center" }}>No records selected. Please go back and select records to print.</p>
                    )}

                </div>
            </div>
        </>
    );
};

export default PrintPdfComponent;